import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Client, PartyRole } from '@hero/schema';
import { SharedTable, type StatusBadgeConfigType } from '../../../app/Table/SharedTable';
import type { SearchInputProps } from '../../../app/Search/SearchInput';
import { ClientRow } from './ClientRow';

export interface ClientRoleFilterConfig {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  options: Array<{
    value: PartyRole;
    label: string;
    count?: number;
    icon?: React.ReactNode;
    className?: string;
  }>;
  counts?: Array<{ value: string; count: number }>;
}

export interface ClientTableFilters {
  showSearch?: boolean;
  roleFilter?: ClientRoleFilterConfig;
}

export interface ClientTableProps {
  clients: Client[];
  isLoading?: boolean;
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
  searchProps?: SearchInputProps;
  onSort?: (column: string) => void;
  onLoadMore?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  filters?: ClientTableFilters;
  /** Empty-state shown only when the list is empty AND no filters/search are active. */
  emptyState?: React.ReactNode;
  /** Set to `true` whenever the user has search / filter / sort applied. */
  hasActiveFilters?: boolean;
  children?: React.ReactNode;
}

const CLIENT_ROLE_CLASSES: Partial<Record<PartyRole, string>> = {
  business_customer: 'bg-primary-100 text-logoBlue',
  individual_customer: 'bg-gray-100 text-gray-600',
  carrier_company: 'bg-emerald-100 text-emerald-700',
  broker: 'bg-amber-100 text-amber-700',
};

const CLIENT_ROLE_BADGE_CONFIG: StatusBadgeConfigType = {
  getStatusClass: (role: string) =>
    CLIENT_ROLE_CLASSES[role as PartyRole] ?? 'bg-gray-100 text-gray-600',
  getStatusDisplayText: (status: string) => status,
};

const ClientTableComponent: React.FC<ClientTableProps> = ({
  clients,
  isLoading,
  sortColumn,
  sortDirection = 'asc',
  searchProps,
  onSort,
  onLoadMore = () => {},
  hasNextPage = false,
  isFetchingNextPage = false,
  filters,
  emptyState,
  hasActiveFilters,
  children,
}) => {
  const { t } = useTranslation(['m-client', 'c-dates']);

  const columns = useMemo(
    () => [
      { key: 'name', label: t('m-client:table.columns.name'), sortable: false },
      { key: 'roles', label: t('m-client:table.columns.roles'), sortable: false },
      { key: 'createdAt', label: t('c-dates:common.created_at'), sortable: false },
      { key: 'actions', label: '', sortable: false },
    ],
    [t],
  );

  return (
    <SharedTable
      data={clients}
      isLoading={isLoading}
      sortColumn={sortColumn}
      sortDirection={sortDirection}
      searchProps={searchProps}
      onSort={onSort}
      statusBadgeConfig={CLIENT_ROLE_BADGE_CONFIG}
      onLoadMore={onLoadMore}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      emptyState={emptyState}
      hasActiveFilters={hasActiveFilters}
      getRowId={(client: Client) => client._id.toString()}>
      {children || <ClientTableHead filters={filters} />}

      <SharedTable.Mid>
        <SharedTable.Rows
          columns={columns}
          rowRender={(client: Client) => <ClientRow client={client} />}
        />
      </SharedTable.Mid>
    </SharedTable>
  );
};

const ClientTableHead: React.FC<{ filters?: ClientTableFilters }> = ({ filters }) => {
  if (!filters) return null;

  return (
    <SharedTable.Head>
      {filters.showSearch !== false && <SharedTable.Search />}
      {filters.roleFilter && (
        <SharedTable.Badge
          options={filters.roleFilter.options}
          counts={filters.roleFilter.counts}
          value={filters.roleFilter.value}
          onChange={filters.roleFilter.onChange}
          showCounts={!!filters.roleFilter.counts}
        />
      )}
    </SharedTable.Head>
  );
};

export const ClientTable = Object.assign(ClientTableComponent, {
  Head: SharedTable.Head,
  Search: SharedTable.Search,
  Badge: SharedTable.Badge,
  Mid: SharedTable.Mid,
  Rows: SharedTable.Rows,
});
